import {
  formatDate,
  formatHours,
  formatMonthShort,
  formatNumber,
  type ReportDocument,
  type ReportRow,
} from '@codepass/shared';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui';

/**
 * The chart on /rapporter/burndown: the hours left of the project's cap - sold plus the change
 * requests in OnePro, as 'Timer af loft' - week by week, the plan stepping down milestone by
 * milestone, and the forecast from today at the recent pace. The hours left go below 0 when more
 * is used than the cap, and the axis follows them down. In green, the cap minus the hours on an
 * invoice in OnePro: the gap down to the hours left is time used and not invoiced yet. The
 * forecast runs to the week the work is expected done - below 0 when it takes more than the cap.
 * Drawn from the report's own `weeks` section, so
 * the chart and the CSV and PDF cannot say different things - the API computes every number, this
 * only lays them out. On the page the chart takes the place of the weekly table. The Burndown tab
 * on /kontrakter/:id draws the same report for the contract's board.
 */

const WIDTH = 800;
const HEIGHT = 300;
/* Room at the top for two rows of marker labels - see `placeMarks`. */
const PAD = { top: 30, right: 16, bottom: 28, left: 56 } as const;
const PLOT_W = WIDTH - PAD.left - PAD.right;
const PLOT_H = HEIGHT - PAD.top - PAD.bottom;

/** A marker label's row height, and about how wide a 10 px character is - a little over half. */
const LABEL_ROW = 12;
const LABEL_CHAR_WIDTH = 5.6;

interface PlacedMark {
  mark: string;
  index: number;
  x: number;
  anchor: 'start' | 'end';
  row: number;
}

/**
 * Where each marker's label goes. To the right of its line, or to the left when it would run out
 * of the chart - the forecast often falls on the last week, and was cut to "Fo". A label that would
 * overlap one already placed drops to the next row, so two markers a week apart both stay readable.
 */
function placeMarks(
  marks: ReadonlyArray<{ mark: string; index: number }>,
  x: (index: number) => number,
): PlacedMark[] {
  const rows: Array<Array<[number, number]>> = [];
  return [...marks]
    .sort((a, b) => a.index - b.index)
    .map(({ mark, index }) => {
      const at = x(index);
      const width = mark.length * LABEL_CHAR_WIDTH;
      const anchor = at + 4 + width > WIDTH - PAD.right ? 'end' : 'start';
      const from = anchor === 'start' ? at + 4 : at - 4 - width;
      const to = from + width;

      let row = rows.findIndex((taken) => taken.every(([a, b]) => to + 6 < a || from > b + 6));
      if (row < 0) {
        rows.push([]);
        row = rows.length - 1;
      }
      rows[row]!.push([from, to]);
      return { mark, index, x: anchor === 'start' ? at + 4 : at - 4, anchor, row };
    });
}

interface Week {
  start: string;
  label: string;
  remaining: number | null;
  cap: number | null;
  spent: number | null;
  forecast: number | null;
  plan: number | null;
  /** Hours on an invoice by the week's end, and the cap minus them - the green line. */
  invoiced: number | null;
  leftToInvoice: number | null;
  marks: string[];
}

const num = (value: ReportRow[string] | undefined): number | null =>
  typeof value === 'number' ? value : null;

/** A round step for the hour axis: 1, 2, 2.5 or 5 times a power of ten. */
function niceStep(raw: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  for (const factor of [1, 2, 2.5, 5, 10]) {
    if (factor * magnitude >= raw) return factor * magnitude;
  }
  return 10 * magnitude;
}

/**
 * The points of one series, split where it has no value, as SVG polyline strings. `stepped` holds
 * each value flat until the next week and drops there - the plan, which falls a whole milestone at
 * a time rather than gliding.
 */
function segments(
  weeks: readonly Week[],
  pick: (week: Week) => number | null,
  x: (index: number) => number,
  y: (value: number) => number,
  stepped = false,
): string[] {
  const lines: string[] = [];
  let current: string[] = [];
  let previous: number | null = null;
  weeks.forEach((week, index) => {
    const value = pick(week);
    if (value === null) {
      if (current.length > 0) lines.push(current.join(' '));
      current = [];
      previous = null;
      return;
    }
    if (stepped && previous !== null && previous !== value) {
      current.push(`${x(index).toFixed(1)},${y(previous).toFixed(1)}`);
    }
    current.push(`${x(index).toFixed(1)},${y(value).toFixed(1)}`);
    previous = value;
  });
  if (current.length > 0) lines.push(current.join(' '));
  return lines;
}

const MARKS: Record<string, { className: string; dash?: string }> = {
  'I dag': { className: 'stroke-ink-muted', dash: '3 3' },
  Deadline: { className: 'stroke-danger' },
  'Sidste milepæl': { className: 'stroke-amber-500', dash: '2 3' },
  'Timer opbrugt': { className: 'stroke-brand', dash: '6 4' },
  'Forventet færdig': { className: 'stroke-success', dash: '2 3' },
};

const PLAN = 'stroke-amber-500';
const INVOICED = 'stroke-success';

function tooltip(week: Week, invoiced: boolean): string {
  const lines = [`${week.label} · ${formatDate(week.start)}`];
  if (week.remaining !== null) lines.push(`Estimattimer tilbage: ${formatHours(week.remaining)}`);
  if (week.plan !== null) lines.push(`Plan: ${formatHours(week.plan)}`);
  if (week.cap !== null) lines.push(`Loft: ${formatHours(week.cap)}`);
  if (invoiced && week.invoiced !== null) {
    lines.push(
      `Faktureret: ${formatHours(week.invoiced)}` +
        (week.leftToInvoice !== null ? ` · loft minus: ${formatHours(week.leftToInvoice)}` : ''),
    );
  }
  if (week.spent !== null) lines.push(`Brugt i ugen: ${formatHours(week.spent)}`);
  if (week.forecast !== null) lines.push(`Forventet: ${formatHours(week.forecast)}`);
  if (week.marks.length > 0) lines.push(week.marks.join(' · '));
  return lines.join('\n');
}

function Legend({
  forecast,
  plan,
  invoiced,
}: {
  forecast: boolean;
  plan: boolean;
  invoiced: boolean;
}) {
  const line = (className: string, dash?: string) => (
    <svg aria-hidden width="22" height="8" className="shrink-0">
      <line
        x1="0"
        y1="4"
        x2="22"
        y2="4"
        className={className}
        strokeWidth="2.5"
        strokeDasharray={dash}
      />
    </svg>
  );
  return (
    <ul className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-ink-muted">
      <li className="flex items-center gap-1.5">{line('stroke-brand')}Estimattimer tilbage</li>
      {plan ? <li className="flex items-center gap-1.5">{line(PLAN)}Plan efter milepæle</li> : null}
      {invoiced ? <li className="flex items-center gap-1.5">{line(INVOICED)}Faktureret</li> : null}
      <li className="flex items-center gap-1.5">{line('stroke-ink-muted/60')}Loft</li>
      {forecast ? (
        <li className="flex items-center gap-1.5">{line('stroke-brand', '6 4')}Forventet</li>
      ) : null}
      <li className="flex items-center gap-1.5">{line('stroke-danger')}Deadline</li>
      <li className="flex items-center gap-1.5">{line('stroke-ink-muted', '3 3')}I dag</li>
    </ul>
  );
}

export function BurndownChart({ report }: { report: ReportDocument }) {
  const section = report.sections.find((entry) => entry.id === 'weeks');
  if (!section) return null;

  const weeks: Week[] = section.rows.map((row) => ({
    start: String(row.start ?? ''),
    label: String(row.week ?? ''),
    remaining: num(row.remaining),
    cap: num(row.cap),
    spent: num(row.spent),
    forecast: num(row.forecast),
    plan: num(row.plan),
    invoiced: num(row.invoiced),
    leftToInvoice: num(row.left_to_invoice),
    marks: typeof row.marks === 'string' && row.marks ? row.marks.split(' · ') : [],
  }));
  const hasForecast = weeks.some((week) => week.forecast !== null);
  /* The forecast runs to the expected finish when there is one, else to the cap running out. */
  const hasFinish = weeks.some((week) => week.marks.includes('Forventet færdig'));
  const hasPlan = weeks.some((week) => week.plan !== null);
  /* Nothing invoiced yet would only be a green line on top of the cap - the caveats say so. */
  const hasInvoiced = weeks.some((week) => (week.invoiced ?? 0) > 0);
  /* Without a cap there is no curve - the report's caveats say why. */
  const hasCap = weeks.some((week) => week.cap !== null);

  if (weeks.length === 0 || !hasCap) {
    return (
      <Card className="mb-5">
        <CardHeader>
          <CardTitle>Burndown</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-xs text-ink-muted">
            {weeks.length === 0
              ? section.empty
              : 'Aftalen har intet loft at brænde ned — se forbeholdene.'}
          </p>
        </CardBody>
      </Card>
    );
  }

  const values = weeks.flatMap((week) => [
    week.remaining ?? 0,
    week.cap ?? 0,
    week.forecast ?? 0,
    week.plan ?? 0,
    week.leftToInvoice ?? 0,
  ]);
  const max = Math.max(1, ...values);
  /* Below 0 is more used than the cap: the axis goes down with it rather than cut it off. */
  const min = Math.min(0, ...values);
  const step = niceStep((max - min) / 4);
  const top = Math.ceil(max / step) * step;
  const bottom = Math.floor(min / step) * step;
  const ticks = Array.from(
    { length: Math.round((top - bottom) / step) + 1 },
    (_, index) => bottom + index * step,
  );

  const slot = PLOT_W / Math.max(weeks.length - 1, 1);
  const x = (index: number): number => PAD.left + index * slot;
  const y = (value: number): number =>
    PAD.top + PLOT_H - ((value - bottom) / (top - bottom)) * PLOT_H;

  /* A label at the first week of each month - every second or third month on a long project. */
  const monthStarts = weeks.flatMap((week, index) =>
    index === 0 || week.start.slice(0, 7) !== weeks[index - 1]!.start.slice(0, 7) ? [index] : [],
  );
  const every = Math.max(1, Math.ceil(monthStarts.length / 12));
  const monthLabels = monthStarts.filter((_, position) => position % every === 0);

  const marks = weeks.flatMap((week, index) =>
    week.marks.filter((mark) => MARKS[mark]).map((mark) => ({ mark, index })),
  );

  return (
    <Card className="mb-5">
      <CardHeader>
        <CardTitle>Burndown</CardTitle>
        <p className="mt-1 text-xs text-ink-muted">
          Estimattimer tilbage af loftet — solgt plus ændringer i OnePro, som på aftalen — ved hver
          uges slutning: de falder med den tid, der bruges, og med det, der mangler af estimatet,
          når en milepæl lukkes.{' '}
          {hasPlan
            ? 'Planen falder med en milepæls estimat, den uge milepælen har deadline — ligger ' +
              'estimattimer tilbage over den, er projektet bagud. '
            : 'Opgaverne har ingen deadlines, så der er ingen plan efter milepæle. '}
          {hasInvoiced
            ? 'Den grønne linje er loftet minus de timer, der er faktureret — afstanden ned til ' +
              'estimattimer tilbage er brugt tid, der endnu ikke er faktureret. '
            : ''}
          {hasForecast
            ? hasFinish
              ? 'Den stiplede linje er forbruget med tempoet de seneste uger, til arbejdet ' +
                'forventes færdigt — estimaterne rettet efter, hvor meget af deres estimat ' +
                'færdige opgaver har brugt. Ender den under 0, rækker loftet ikke.'
              : 'Den stiplede linje er forbruget med tempoet de seneste uger, til timerne er opbrugt.'
            : 'Der er ingen forventet kurve — se forbeholdene.'}{' '}
          Hold musen over en uge for tallene.
        </p>
        <div className="mt-2">
          <Legend forecast={hasForecast} plan={hasPlan} invoiced={hasInvoiced} />
        </div>
      </CardHeader>
      <CardBody>
        <div className="overflow-x-auto">
          <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            className="h-auto w-full min-w-[640px]"
            role="img"
            aria-label={`Burndown, ${weeks.length} uger`}
          >
            {ticks.map((tick) => (
              <g key={tick}>
                <line
                  x1={PAD.left}
                  x2={WIDTH - PAD.right}
                  y1={y(tick)}
                  y2={y(tick)}
                  className="stroke-line"
                />
                <text
                  x={PAD.left - 8}
                  y={y(tick) + 4}
                  textAnchor="end"
                  className="fill-ink-muted text-[11px] tabular"
                >
                  {formatNumber(tick, Number.isInteger(tick) ? 0 : 1)}
                </text>
              </g>
            ))}

            {monthLabels.map((index) => (
              <text
                key={index}
                x={x(index)}
                y={HEIGHT - 8}
                textAnchor="middle"
                className="fill-ink-muted text-[11px]"
              >
                {formatMonthShort(weeks[index]!.start)}
                {weeks[index]!.start.slice(5, 7) === '01' || index === 0
                  ? ` ${weeks[index]!.start.slice(0, 4)}`
                  : ''}
              </text>
            ))}

            {placeMarks(marks, x).map((placed) => {
              const style = MARKS[placed.mark]!;
              return (
                <g key={`${placed.mark}-${placed.index}`}>
                  <line
                    x1={x(placed.index)}
                    x2={x(placed.index)}
                    y1={PAD.top}
                    y2={PAD.top + PLOT_H}
                    className={style.className}
                    strokeWidth="1.5"
                    strokeDasharray={style.dash}
                  />
                  <text
                    x={placed.x}
                    y={LABEL_ROW * (placed.row + 1) - 2}
                    textAnchor={placed.anchor}
                    className="fill-ink-muted text-[10px]"
                  >
                    {placed.mark}
                  </text>
                </g>
              );
            })}

            {segments(weeks, (week) => week.cap, x, y).map((points) => (
              <polyline
                key={`cap-${points}`}
                points={points}
                fill="none"
                className="stroke-ink-muted/60"
                strokeWidth="1.5"
              />
            ))}
            {segments(weeks, (week) => week.plan, x, y, true).map((points) => (
              <polyline
                key={`plan-${points}`}
                points={points}
                fill="none"
                className={PLAN}
                strokeWidth="2"
              />
            ))}
            {hasInvoiced
              ? segments(weeks, (week) => week.leftToInvoice, x, y).map((points) => (
                  <polyline
                    key={`invoiced-${points}`}
                    points={points}
                    fill="none"
                    className={INVOICED}
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                  />
                ))
              : null}
            {segments(weeks, (week) => week.forecast, x, y).map((points) => (
              <polyline
                key={`forecast-${points}`}
                points={points}
                fill="none"
                className="stroke-brand"
                strokeWidth="2"
                strokeDasharray="6 4"
              />
            ))}
            {segments(weeks, (week) => week.remaining, x, y).map((points) => (
              <polyline
                key={`remaining-${points}`}
                points={points}
                fill="none"
                className="stroke-brand"
                strokeWidth="2.5"
                strokeLinejoin="round"
              />
            ))}

            {/* One column per week for the numbers under the mouse. */}
            {weeks.map((week, index) => (
              <rect
                key={week.start}
                x={x(index) - slot / 2}
                y={PAD.top}
                width={slot}
                height={PLOT_H}
                fill="transparent"
                className="hover:fill-brand/5"
              >
                <title>{tooltip(week, hasInvoiced)}</title>
              </rect>
            ))}
          </svg>
        </div>
      </CardBody>
    </Card>
  );
}
